import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Splitting from "splitting";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/feature/Seo";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import referenzenHeroAsset from "@/assets/referenzen/referenzen-hero-bg.png";
import pvAnlageEinfamilienhausAsset from "@/assets/referenzen/pv-anlage-einfamilienhaus.jpg";
import pvIndustriedachAsset from "@/assets/referenzen/pv-industriedach.jpg";
import stromspeicherNachruestungAsset from "@/assets/referenzen/stromspeicher-nachruestung.jpg";
import speicherMehrfamilienhausAsset from "@/assets/referenzen/speicher-mehrfamilienhaus.jpg";
import wallboxPvAnbindungAsset from "@/assets/referenzen/wallbox-pv-anbindung.jpg";
import ladeparkGewerbeAsset from "@/assets/referenzen/ladepark-gewerbe.jpg";
import waermepumpeElektrischAsset from "@/assets/referenzen/waermepumpe-elektrisch.jpg";
import smartHomeEinfamilienhausAsset from "@/assets/referenzen/smart-home-einfamilienhaus.jpg";
import eCheckGewerbeAsset from "@/assets/referenzen/e-check-gewerbe.jpg";
import ersatzstromEinfamilienhausAsset from "@/assets/referenzen/ersatzstrom-einfamilienhaus.jpg";
import pvLandwirtschaftlicheHalleAsset from "@/assets/referenzen/pv-landwirtschaftliche-halle.jpg";
import elektroinstallationNeubauGewerbeAsset from "@/assets/referenzen/elektroinstallation-neubau-gewerbe.jpg";
import referenzenLeistungenAsset from "@/assets/referenzen-leistungen-schaltschrank.png";

gsap.registerPlugin(ScrollTrigger);

/* ────────────────── Reveal hooks ────────────────── */

function useSplitReveal(selector: string) {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const targets = Array.from(document.querySelectorAll<HTMLElement>(selector));
    if (!targets.length) return;
    const triggers: ScrollTrigger[] = [];

    targets.forEach((el) => {
      if (prefersReduced) {
        gsap.fromTo(
          el,
          { opacity: 0 },
          {
            opacity: 1,
            duration: 0.6,
            scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
          }
        );
        return;
      }
      const result = Splitting({ target: el, by: "words" });
      const words = result[0]?.words ?? [];
      if (!words.length) return;
      gsap.set(words, { opacity: 0, y: 20, display: "inline-block" });
      const tween = gsap.to(words, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power3.out",
        stagger: 0.04,
        scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
      });
      if (tween.scrollTrigger) triggers.push(tween.scrollTrigger);
    });

    return () => triggers.forEach((t) => t.kill());
  }, [selector]);
}


/* ────────────────── Projekt-Daten (PLATZHALTER) ──────────────────
   {{ECHTES_PROJEKT}} — Die folgenden 12 Projekte sind Beispieltexte.
   Echte Projektdaten + Fotos müssen vom Kunden geliefert und hier
   eingesetzt werden. Ort bleibt bewusst generisch ("Emmerich am Rhein
   und Umgebung") — keine erfundenen Adressen oder Kundennamen.
*/

const projekte = [
  {
    category: "PHOTOVOLTAIK",
    title: "PV-ANLAGE EINFAMILIENHAUS",
    text: "Komplette Solaranlage mit Speicher, geplant und installiert aus einer Hand.",
    image: pvAnlageEinfamilienhausAsset,
    imageAlt: "Photovoltaik-Installation am Einfamilienhaus durch einen Elektrotechniker auf dem Dach in Emmerich am Rhein und Umgebung",
  },
  {
    category: "PHOTOVOLTAIK",
    title: "PV AUF INDUSTRIEDACH",
    text: "Großflächige Photovoltaik-Installation für einen Gewerbebetrieb.",
    image: pvIndustriedachAsset,
    imageAlt: "Photovoltaik-Anlage auf dem Flachdach eines Gewerbegebäudes in Emmerich am Rhein und Umgebung",
  },
  {
    category: "SPEICHER",
    title: "STROMSPEICHER-NACHRÜSTUNG",
    text: "Bestehende PV-Anlage um einen modernen Batteriespeicher erweitert.",
    image: stromspeicherNachruestungAsset,
    imageAlt: "Batteriespeicher und Wechselrichter in einem Technikraum in Emmerich am Rhein und Umgebung",
  },
  {
    category: "SPEICHER",
    title: "SPEICHER MEHRFAMILIENHAUS",
    text: "Zentraler Stromspeicher für ein Mehrfamilienhaus mit Mieterstrom.",
    image: speicherMehrfamilienhausAsset,
    imageAlt: "Zentraler Stromspeicher und Schaltschrank in einem Technikraum eines Mehrfamilienhauses in Emmerich am Rhein und Umgebung",
  },
  {
    category: "WALLBOX",
    title: "WALLBOX MIT PV-ANBINDUNG",
    text: "Ladelösung intelligent verbunden mit der eigenen Solaranlage.",
    image: wallboxPvAnbindungAsset,
    imageAlt: "Wallbox an einer Backsteinwand mit einem aufladenden Elektroauto in Emmerich am Rhein und Umgebung",
  },
  {
    category: "WALLBOX",
    title: "LADEPARK GEWERBE",
    text: "Mehrere Wallboxen für den Fuhrpark eines Betriebs.",
    image: ladeparkGewerbeAsset,
    imageAlt: "Mehrere E-Autos laden an Wallboxen auf einem Firmenparkplatz vor einem Gewerbegebäude in Emmerich am Rhein und Umgebung",
  },
  {
    category: "WAERMEPUMPE",
    title: "WÄRMEPUMPE ELEKTRISCH EINGEBUNDEN",
    text: "Fachgerechte elektrische Einbindung einer Wärmepumpe ins Hausnetz.",
    image: waermepumpeElektrischAsset,
    imageAlt: "Schwarze Außeneinheit einer Wärmepumpe an einer Hauswand mit Stromanschluss in Emmerich am Rhein und Umgebung",
  },
  {
    category: "SMART HOME",
    title: "SMART-HOME EINFAMILIENHAUS",
    text: "Beleuchtung, Beschattung und Energie zentral und intelligent gesteuert.",
    image: smartHomeEinfamilienhausAsset,
    imageAlt: "Smart-Home-Steuerpanel an einer Wand im hellen Wohnzimmer eines Einfamilienhauses in Emmerich am Rhein und Umgebung",
  },
  {
    category: "E-CHECK",
    title: "E-CHECK GEWERBEBETRIEB",
    text: "Normgerechte Prüfung der Elektroinstallation mit vollständiger Dokumentation.",
    image: eCheckGewerbeAsset,
    imageAlt: "Elektriker prüft einen Schaltschrank mit Messgerät im Gewerbebetrieb in Emmerich am Rhein und Umgebung",
  },
  {
    category: "NOTSTROM",
    title: "ERSATZSTROM EINFAMILIENHAUS",
    text: "Absicherung gegen Stromausfall mit automatischer Umschaltung.",
    image: ersatzstromEinfamilienhausAsset,
    imageAlt: "Wechselrichter und Sicherungskasten mit ordentlich verlegten Kabeln im Technikraum eines Einfamilienhauses in Emmerich am Rhein und Umgebung",
  },
  {
    category: "LANDWIRTSCHAFT",
    title: "PV AUF LANDWIRTSCHAFTLICHER HALLE",
    text: "Große Dachfläche eines Hofs für die eigene Stromerzeugung genutzt.",
    image: pvLandwirtschaftlicheHalleAsset,
    imageAlt: "Luftaufnahme einer landwirtschaftlichen Halle mit großer Photovoltaik-Anlage auf dem Dach in Emmerich am Rhein und Umgebung",
  },
  {
    category: "GEWERBE",
    title: "ELEKTROINSTALLATION NEUBAU GEWERBE",
    text: "Komplette Elektroinstallation für einen gewerblichen Neubau.",
    image: elektroinstallationNeubauGewerbeAsset,
    imageAlt: "Elektriker installiert Schaltschränke und Leitungen in einem gewerblichen Neubau in Emmerich am Rhein und Umgebung",
  },
];

/* {{ECHTE_TESTIMONIALS}} — Platzhalter-Zitate, später durch echte
   Google-Rezensionen / freigegebene Kundenstimmen ersetzen. */
const testimonials = [
  {
    quote:
      "Sauber geplant, sauber installiert, sauber erklärt. Genau so stellt man sich Handwerk vor.",
    name: "Kunde",
    ort: "Emmerich am Rhein und Umgebung",
  },
  {
    quote:
      "Ein Ansprechpartner, ein Team, ein Ergebnis. Wir würden jederzeit wieder mit Zweering bauen.",
    name: "Kundin",
    ort: "Emmerich am Rhein und Umgebung",
  },
];

export default function Referenzen() {
  useSmoothScroll();
  useSplitReveal("[data-split-reveal]");

  const categories = useMemo(() => {
    const set = new Set<string>();
    projekte.forEach((p) => set.add(p.category));
    return ["ALLE", ...Array.from(set)];
  }, []);
  const [activeCategory, setActiveCategory] = useState<string>("ALLE");
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    setPrefersReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  const filtered = useMemo(
    () => (activeCategory === "ALLE" ? projekte : projekte.filter((p) => p.category === activeCategory)),
    [activeCategory]
  );

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Archivo, sans-serif" }}>
      <Seo
        title="Referenzen & Projekte | Zweering Elektrotechnik Emmerich am Rhein"
        description="Ein Auszug unserer Projekte in Emmerich am Rhein und Umgebung — Photovoltaik, Speicher, Wallbox und Elektrotechnik für Privat, Gewerbe und Landwirtschaft."
        canonical="https://zweering.de/referenzen"
        breadcrumbs={[
          { name: "Startseite", url: "https://zweering.de/" },
          { name: "Referenzen", url: "https://zweering.de/referenzen" },
        ]}
      />
      <Header />

      <main id="main-content">

        {/* 1. HERO (~60vh) */}
        <section className="relative h-[60vh] min-h-[460px] w-full overflow-hidden">
          <img
            src={referenzenHeroAsset}
            alt="Modernes Gewerbegebäude mit Photovoltaik-Anlage auf dem Flachdach in Emmerich am Rhein"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
          <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-10 lg:px-14 pb-16 md:pb-20">
            <div className="max-w-7xl mx-auto w-full">
              <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
                REFERENZEN
              </p>
              <h1
                data-split-reveal
                className="font-bold uppercase text-white text-[30px] sm:text-[40px] md:text-[64px] lg:text-[84px] leading-[0.95] max-w-5xl"
                style={{ letterSpacing: "-2.4px" }}
              >
                Projekte,<br />die für sich sprechen
              </h1>
              <p className="text-white/85 text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
                Ein Auszug unserer Arbeit in Emmerich am Rhein und Umgebung — vom
                Einfamilienhaus bis zum Gewerbebetrieb.
              </p>
            </div>
          </div>
        </section>

        {/* 2. PROJEKT-GRID */}
        <section className="bg-white py-36 md:py-40 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto">
            <h2
              data-split-reveal
              className="font-bold uppercase text-[#0A0A0A] leading-[0.95] max-w-3xl break-words"
              style={{ fontSize: "clamp(1.5rem, 6vw, 3.5rem)", letterSpacing: "clamp(-0.5px, -0.15vw, -2px)" }}
            >
              Ein Auszug unserer Projekte
            </h2>

            {/* Filterleiste */}
            <LayoutGroup id="referenzen-filter">
              <div
                role="tablist"
                aria-label="Projekte nach Kategorie filtern"
                className="mt-10 md:mt-12 flex flex-wrap gap-2 md:gap-3"
              >
                {categories.map((cat) => {
                  const isActive = cat === activeCategory;
                  return (
                    <button
                      key={cat}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveCategory(cat)}
                      className={`relative rounded-full px-5 py-2.5 text-[11px] md:text-xs uppercase tracking-widest font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50] focus-visible:ring-offset-2 ${
                        isActive ? "text-white" : "text-[#0A0A0A] hover:text-[#4CAF50]"
                      }`}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="referenzen-filter-pill"
                          className="absolute inset-0 rounded-full bg-[#0A0A0A]"
                          transition={{ type: "spring", stiffness: 400, damping: 32 }}
                        />
                      )}
                      {!isActive && (
                        <span className="absolute inset-0 rounded-full border border-[#0A0A0A]/15" />
                      )}
                      <span className="relative z-10">{cat}</span>
                    </button>
                  );
                })}
              </div>
            </LayoutGroup>

            <div className="mt-10 md:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <AnimatePresence mode="popLayout">
                {filtered.map((p, idx) => (
                  <motion.article
                    key={`${p.category}-${p.title}`}
                    layout
                    initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.98 }}
                    whileInView={prefersReduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
                    exit={prefersReduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{
                      duration: 0.5,
                      ease: [0.22, 1, 0.36, 1],
                      delay: prefersReduced ? 0 : (idx % 3) * 0.08,
                    }}
                    className="group rounded-2xl bg-white overflow-hidden flex flex-col"
                  >
                    {/* Projekt-Bild */}
                    <div className="rounded-2xl overflow-hidden bg-[#F4F4F4] aspect-[4/3] mb-5">
                      <img
                        src={p.image}
                        alt={p.imageAlt}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                      />
                    </div>
                    <p className="text-[#4CAF50] uppercase text-[11px] md:text-xs tracking-widest font-medium mb-2">
                      {p.category}
                    </p>
                    <h3
                      className="font-bold uppercase text-[#0A0A0A] text-[20px] md:text-[24px] leading-[1.1]"
                      style={{ letterSpacing: "-0.8px" }}
                    >
                      {p.title}
                    </h3>
                    <p className="mt-3 text-foreground-600 text-base leading-relaxed">{p.text}</p>
                    <p className="mt-4 text-foreground-600 text-sm uppercase tracking-wider">
                      Emmerich am Rhein und Umgebung
                    </p>
                  </motion.article>
                ))}
              </AnimatePresence>
            </div>

            <p className="mt-12 text-foreground-600 text-sm italic">
              {/* Hinweis für späteren Content-Pflege-Schritt */}
              {/* Diese 6 Projekte sind Platzhalter — echte Projektdaten und Fotos einsetzen. */}
            </p>
          </div>
        </section>

        {/* 3. TRUST-BLOCK */}
        <section className="bg-[#F4F4F4] py-36 md:py-40 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 items-start">
              <div>
                <p className="text-[#4CAF50] text-xs uppercase tracking-widest font-medium mb-3">
                  Vertrauen
                </p>
                <p
                  className="font-bold uppercase text-[#0A0A0A] text-[30px] sm:text-[40px] md:text-[64px] leading-[0.95]"
                  style={{ letterSpacing: "-2px" }}
                >
                  ★ 5,0
                </p>
                <p className="mt-2 text-foreground-600 text-sm uppercase tracking-wider">
                  Google-Bewertung
                </p>
                <p className="mt-4 text-foreground-600 text-base leading-relaxed">
                  Was unsere Kunden über uns sagen.
                </p>
              </div>

              {testimonials.map((t, i) => (
                <figure
                  key={i}
                  className="rounded-2xl bg-white p-8 md:p-10 flex flex-col h-full"
                >
                  <blockquote
                    className="text-[#0A0A0A] text-lg md:text-xl leading-snug font-medium"
                    style={{ letterSpacing: "-0.3px" }}
                  >
                    „{t.quote}“
                  </blockquote>
                  <figcaption className="mt-6 text-foreground-600 text-sm uppercase tracking-wider">
                    {t.name} — {t.ort}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 4. CTA */}
        <section className="bg-[#4CAF50] py-36 md:py-40 px-6 md:px-10 lg:px-14">
          <div className="max-w-5xl mx-auto text-center">
            <h2
              data-split-reveal
              className="font-bold uppercase text-white text-[36px] sm:text-[52px] md:text-[72px] lg:text-[88px] leading-[0.95]"
              style={{ letterSpacing: "-2.4px" }}
            >
              Ihr Projekt könnte das nächste sein
            </h2>
            <p className="mt-6 text-white/90 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Kostenlose Erstberatung — erzählen Sie uns von Ihrem Vorhaben.
            </p>
            <a
              href="/kontakt"
              className="inline-flex items-center mt-10 px-8 py-4 rounded-full bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#0A0A0A]/85 transition-colors"
            >
              Erstberatung anfragen
            </a>
          </div>
        </section>

        {/* 5. CROSS-LINK */}
        <section className="bg-[#F4F4F4] py-36 md:py-40 px-6 md:px-10 lg:px-14">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="rounded-2xl overflow-hidden bg-white aspect-[4/3] md:order-1 order-2">
              <img
                src={referenzenLeistungenAsset}
                alt="Professionell verdrahteter Elektro-Schaltschrank von Zweering Elektrotechnik"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="md:order-2 order-1">
              <p className="text-[#4CAF50] uppercase text-xs md:text-sm tracking-widest mb-4 font-medium">
                Das könnte Sie auch interessieren
              </p>
              <h2
                data-split-reveal
                className="font-bold uppercase text-[#0A0A0A] text-[32px] md:text-[48px] lg:text-[56px] leading-[0.95]"
                style={{ letterSpacing: "-2px" }}
              >
                Unsere Leistungen
              </h2>
              <p className="mt-6 text-foreground-600 text-base md:text-lg leading-relaxed">
                Von Photovoltaik bis Smart Home — sehen Sie, womit wir Ihr Zuhause
                ausstatten.
              </p>
              <a
                href="/photovoltaik"
                className="inline-flex items-center mt-8 px-6 py-3.5 rounded-full bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#4CAF50] transition-colors"
              >
                Zu den Leistungen →
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
